/* pp-optin-v2-app.js
   PillsandPowders opt-in v2. Drives both preview variants.

   Variant A is button driven: two big CTAs open a modal carrying step 1 (multi-select)
   and step 2 (contact fields). There is no form and no name or email input on the page
   itself. The popup NEVER opens by itself: no load trigger, no timer, no exit intent.
   The only way in is a [data-ppv2-open] button.

   Variant B is form driven: the same step machine rendered open on the page. Step 1 is
   visible on load, step 2 appears after Continue. No popup on that page at all.

   Both surfaces run the same wireStepper function. The only difference is whether a
   modal wrapper is present, which adds open, close, focus trap and scroll lock.

   On the live page both surfaces ship in one document and the visitor is assigned to one
   of them, 50/50 and sticky. The assignment itself is done by the inline script in
   sections/pp-optin-v2-split.liquid, which runs before the surfaces are parsed so the
   losing one never paints. This file then DELETES the losing surface from the DOM before
   anything is wired, so there is exactly one form, one set of inputs and one submit on
   the page, whichever variant the visitor drew. The served HTML is byte-identical for
   everyone, so the full-page cache cannot split-brain the test.

   Same mechanism family as the live v1 opt-in: a plain form that talks straight to the
   Klaviyo client subscriptions endpoint, no app and no embed.

   STAGED MODE (config.staged === true) is the preview default: the payload is built
   and shown on screen, and nothing leaves the browser. Flip config.staged to false and
   fill klaviyoListId + nextUrl to go live. */

(function () {
  var CONFIG_DEFAULTS = {
    staged: true,
    klaviyoCompanyId: '',
    klaviyoListId: '',
    klaviyoRevision: '2025-07-15',
    customSource: 'Buyers Guide Optin v2',
    supplementsProperty: 'pp_supplements_current',
    supplementsLabelsProperty: 'pp_supplements_current_labels',
    supplementsCountProperty: 'pp_supplements_current_count',
    requireSelection: false,
    nextUrl: ''
  };

  // The variant stamped on <html> by the split section, or '' on a single-variant page.
  function assignedVariant() {
    var v = document.documentElement.getAttribute('data-ppv2-variant');
    return v === 'a' || v === 'b' ? v : '';
  }

  function readConfig() {
    var node = document.querySelector('script[data-pp-optin-v2-config]');
    var parsed = {};
    if (node) {
      try { parsed = JSON.parse(node.textContent) || {}; } catch (err) { parsed = {}; }
    }
    var config = {};
    for (var key in CONFIG_DEFAULTS) {
      if (Object.prototype.hasOwnProperty.call(CONFIG_DEFAULTS, key)) {
        config[key] = Object.prototype.hasOwnProperty.call(parsed, key) ? parsed[key] : CONFIG_DEFAULTS[key];
      }
    }
    // Hard lock set by the section on any preview- handle. It can only force staging
    // on, never off, so the preview pages stay inert whatever the config says.
    if (window.PP_OPTIN_V2_FORCE_STAGED === true) config.staged = true;
    return config;
  }

  var CONFIG = readConfig();

  function attributionSource() {
    var params = new URLSearchParams(window.location.search);
    var cookie = (document.cookie.match(/(?:^|;\s*)pp_ref=([^;]+)/) || [])[1];
    return params.get('utm_source') || cookie || 'direct';
  }

  function attributionProps() {
    var params = new URLSearchParams(window.location.search);
    var props = { buyers_guide_source: attributionSource() };
    if (params.get('utm_medium')) props.buyers_guide_utm_medium = params.get('utm_medium');
    if (params.get('utm_campaign')) props.buyers_guide_utm_campaign = params.get('utm_campaign');
    return props;
  }

  function buildPayload(email, firstName, selection, consent) {
    var props = attributionProps();
    if (selection) {
      props[CONFIG.supplementsProperty] = selection.map(function (item) { return item.value; });
      props[CONFIG.supplementsLabelsProperty] = selection.map(function (item) { return item.label; });
      props[CONFIG.supplementsCountProperty] = selection.length;
    }
    var variant = assignedVariant();
    if (variant) props.pp_optin_variant = variant;

    // The marketing choice travels with the profile, together with the exact sentence
    // the visitor was shown, so the consent record can be read back later.
    props.pp_marketing_consent = !!(consent && consent.marketing);
    if (consent && consent.text) props.pp_marketing_consent_text = consent.text;
    props.pp_marketing_consent_at = new Date().toISOString();

    var attributes = {
      email: email,
      first_name: firstName,
      properties: props
    };
    // Only claim a marketing subscription when one was actually given. A declined
    // visitor still reaches the list, because the list add is what triggers the report
    // delivery email, and that email is the thing they asked for rather than marketing.
    // Klaviyo's client endpoint accepts no consent value other than SUBSCRIBED, so the
    // decline is carried by pp_marketing_consent and the sending side filters on it.
    attributes.subscriptions = consent && consent.marketing
      ? { email: { marketing: { consent: 'SUBSCRIBED' } } }
      : {};

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: CONFIG.customSource + variant.toUpperCase(),
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: { list: { data: { type: 'list', id: CONFIG.klaviyoListId } } }
      }
    };
  }

  function subscribe(payload) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + CONFIG.klaviyoCompanyId, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: CONFIG.klaviyoRevision },
      body: JSON.stringify(payload),
      keepalive: true
    });
  }

  function validEmail(value) {
    // Deliberately loose, same spirit as v1: reject the obvious typos, never a real
    // address. The dot has to come after the @, so first.last@domain.com passes.
    var at = value.indexOf('@');
    var dot = value.lastIndexOf('.');
    return at > 0 && dot > at + 1 && dot < value.length - 1 && !/\s/.test(value);
  }

  function goLive(payload, email, firstName, button) {
    if (button) { button.disabled = true; button.textContent = 'Sending...'; }
    try { subscribe(payload); } catch (err) { /* fire and forget, same as v1 */ }
    try {
      Shopify.analytics.publish('lead', { email: email, first_name: firstName });
    } catch (err) { /* analytics is optional */ }
    setTimeout(function () { window.location.href = CONFIG.nextUrl; }, 350);
  }

  // Shared by every surface: read the checked options in DOM order.
  function readSelection(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.pp-optin-v2-option input'))
      .filter(function (input) { return input.checked; })
      .map(function (input) {
        return { value: input.value, label: input.getAttribute('data-ppv2-label') };
      });
  }

  // The marketing box and the sentence printed next to it, read at submit time.
  function readConsent(root) {
    var box = root.querySelector('[data-ppv2-marketing]');
    return {
      marketing: !!(box && box.checked),
      text: box ? box.getAttribute('data-ppv2-consent-text') || '' : ''
    };
  }

  function countText(n) {
    if (n === 0) return 'Nothing selected yet. You can pick as many as apply.';
    if (n === 1) return '1 selected.';
    return n + ' selected.';
  }

  function wireOptionToggles(root, onChange) {
    Array.prototype.forEach.call(root.querySelectorAll('.pp-optin-v2-option input'), function (input) {
      input.addEventListener('change', function () {
        input.closest('.pp-optin-v2-option').classList.toggle('is-selected', input.checked);
        onChange();
      });
    });
  }

  /* ---------------- variant A: the popup ---------------- */

  var FOCUSABLE = 'button:not([hidden]):not([disabled]), [href], input:not([type=checkbox]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  function wireStepper(root, modal) {
    var dialog = modal ? modal.querySelector('.pp-optin-v2-dialog') : null;
    var steps = {
      select: root.querySelector('[data-ppv2-step="select"]'),
      email: root.querySelector('[data-ppv2-step="email"]'),
      done: root.querySelector('[data-ppv2-step="done"]')
    };
    var countNode = root.querySelector('[data-ppv2-count]');
    var chipsNode = root.querySelector('[data-ppv2-chips]');
    var barNode = root.querySelector('[data-ppv2-bar]');
    var stepLabel = root.querySelector('[data-ppv2-steplabel]');
    var continueBtn = root.querySelector('[data-ppv2-continue]');
    var submitBtn = root.querySelector('[data-ppv2-submit]');
    var backBtn = root.querySelector('[data-ppv2-back]');
    var foot = root.querySelector('[data-ppv2-foot]');
    var form = root.querySelector('form');
    var msg = root.querySelector('[data-ppv2-msg]');
    var selectMsg = root.querySelector('[data-ppv2-selectmsg]');
    var stagedNode = root.querySelector('[data-ppv2-stagedpayload]');
    var body = root.querySelector('.pp-optin-v2-dialog-body');
    var lastFocus = null;

    function refreshCount() {
      if (countNode) countNode.textContent = countText(readSelection(root).length);
    }

    function show(name) {
      Object.keys(steps).forEach(function (key) {
        if (steps[key]) steps[key].classList.toggle('is-active', key === name);
      });
      if (barNode) barNode.style.width = name === 'select' ? '50%' : '100%';
      if (stepLabel) stepLabel.textContent = name === 'done' ? 'Done' : (name === 'select' ? 'Step 1 of 2' : 'Step 2 of 2');
      if (continueBtn) continueBtn.hidden = name !== 'select';
      if (submitBtn) submitBtn.hidden = name !== 'email';
      if (foot) foot.hidden = name === 'done';
      if (body) body.scrollTop = 0;
      root.setAttribute('data-ppv2-active-step', name);
      // On the page (variant B) the card changes height between steps, so bring its
      // top back into view instead of leaving the visitor mid-card.
      if (!modal && name !== 'select') {
        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    }

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      modal.classList.add('is-open');
      document.documentElement.classList.add('pp-optin-v2-lock');
      if (dialog) dialog.focus({ preventScroll: true });
    }

    function close() {
      modal.classList.remove('is-open');
      modal.hidden = true;
      document.documentElement.classList.remove('pp-optin-v2-lock');
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    if (modal) {
      // The only entry points. No load trigger, no timer, no exit intent.
      Array.prototype.forEach.call(document.querySelectorAll('[data-ppv2-open]'), function (trigger) {
        trigger.addEventListener('click', open);
      });
      Array.prototype.forEach.call(modal.querySelectorAll('[data-ppv2-close], [data-ppv2-backdrop]'), function (node) {
        node.addEventListener('click', close);
      });
    }

    if (modal) document.addEventListener('keydown', function (event) {
      if (!modal.classList.contains('is-open')) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      var items = Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    wireOptionToggles(root, function () {
      if (selectMsg) selectMsg.classList.remove('is-visible');
      refreshCount();
    });

    var marketingBox = root.querySelector('[data-ppv2-marketing]');
    if (marketingBox) {
      marketingBox.addEventListener('change', function () {
        marketingBox.closest('.pp-optin-v2-consentbox')
          .classList.toggle('is-checked', marketingBox.checked);
      });
    }

    if (continueBtn) {
      continueBtn.addEventListener('click', function () {
        var picked = readSelection(root);
        if (CONFIG.requireSelection && picked.length === 0) {
          if (selectMsg) selectMsg.classList.add('is-visible');
          return;
        }
        if (chipsNode) {
          chipsNode.innerHTML = '';
          if (picked.length === 0) {
            var none = document.createElement('span');
            none.className = 'pp-optin-v2-chip pp-optin-v2-chip--none';
            none.textContent = 'No supplements selected';
            chipsNode.appendChild(none);
          } else {
            picked.forEach(function (item) {
              var chip = document.createElement('span');
              chip.className = 'pp-optin-v2-chip';
              chip.textContent = item.label;
              chipsNode.appendChild(chip);
            });
          }
        }
        show('email');
        var emailInput = root.querySelector('input[type=email]');
        if (emailInput) emailInput.focus({ preventScroll: true });
      });
    }

    if (backBtn) backBtn.addEventListener('click', function () { show('select'); });

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var nameInput = form.querySelector('input[name=first_name]');
        var emailInput = form.querySelector('input[type=email]');
        var firstName = ((nameInput && nameInput.value) || '').trim();
        var email = ((emailInput && emailInput.value) || '').trim();
        if (!firstName || !email || !validEmail(email)) {
          if (msg) msg.classList.add('is-visible');
          return;
        }
        if (msg) msg.classList.remove('is-visible');
        var payload = buildPayload(email, firstName, readSelection(root), readConsent(root));

        if (CONFIG.staged) {
          if (stagedNode) stagedNode.textContent = JSON.stringify(payload, null, 2);
          root.setAttribute('data-ppv2-submitted', 'staged');
          show('done');
          return;
        }
        goLive(payload, email, firstName, submitBtn);
      });
    }

    refreshCount();
    show('select');
  }

  function wireScrollTriggers() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ppv2-scrollto]'), function (trigger) {
      trigger.addEventListener('click', function () {
        var target = document.getElementById(trigger.getAttribute('data-ppv2-scrollto'));
        if (!target) return;
        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        target.focus({ preventScroll: true });
      });
    });
  }

  // On the split page, delete the surface the visitor was not assigned to. This runs
  // before anything is wired, so the losing variant leaves behind no inputs, no form and
  // no triggers: from here on the page behaves exactly like a single-variant page.
  function pruneSurfaces() {
    var variant = assignedVariant();
    if (!variant) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-ppv2-surface]'), function (node) {
      if (node.getAttribute('data-ppv2-surface') !== variant) node.remove();
    });
  }

  function init() {
    pruneSurfaces();
    var modal = document.querySelector('[data-pp-optin-v2-modal]');
    if (modal) wireStepper(modal, modal);
    var stepper = document.querySelector('[data-pp-optin-v2-stepper]');
    if (stepper) wireStepper(stepper, null);
    wireScrollTriggers();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
